import { EField as EContactField } from "@rentiohq/shared-frontend/dist/forms/addContact/schema.addContact.types";
import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import {
  IValues,
  getValue,
  mapNullableBooleanValueToFormField,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
  truncateEAN,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { EDocumentPackageStatus } from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import {
  EPropertyEPCLabel,
  EPropertyTypeId,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationContractDestination,
  ERegistrationDocumentType,
  ERegistrationMemberType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  getAllRequiredCompanyFields,
  getAllRequiredContactFields,
} from "@rentiohq/shared-frontend/dist/utils/forms.utils";
import { getStore } from "@rentiohq/shared-frontend/dist/utils/redux/redux.utils";
import { orderBy } from "lodash";
import { EField, IExtraData } from "./schema.registration.types";

const contractLeaseType: { [key: string]: string } = {
  student_housing: ERegistrationContractDestination.Housing,
  commercial_lease: ERegistrationContractDestination.Commercial,
};
// Helper to get Brussels-specific property/amenity values
const getBrusselsPropertyValues = (extraData: IExtraData): IValues => {
  const { property, propertyAmenities, contract } = extraData;

  return {
    // Brussels registration fields => contract
    [EField.IsSharedLease]: mapNullableBooleanValueToFormField(
      contract.isSharedLease,
    ),
    [EField.ReferencePrice]: contract.referencePrice,

    // Brussels registration field => property
    // General
    [EField.ConstructionYear]: property.constructionYear,

    // EPC Fields
    [EEpcField.Label]: [
      EPropertyEPCLabel.NoEPCAvailable,
      EPropertyEPCLabel.Unknown,
    ].includes(property.epcLabel || EPropertyEPCLabel.Unknown)
      ? undefined
      : property.epcLabel,
    [EEpcField.Value]: property.epcValue,
    [EEpcField.ExpirationDate]: property.epcExpirationDate,
    [EEpcField.CertificateId]: property.epcCertificateId,

    // Electricity
    [EField.ElectricityType]: property.electricityType,
    [EField.ElectricityEan]: truncateEAN(property.electricityEan),
    [EField.ElectricityMeterNumberBoth]: property.electricityMeterNumberBoth,
    [EField.ElectricityMeterType]: property.electricityMeterType,

    // Natural Gas
    [EField.NaturalGasAvailable]: property.naturalGasAvailable,
    [EField.NaturalGasEan]: truncateEAN(property.naturalGasEan),
    [EField.NaturalGasMeterNumber]: property.naturalGasMeterNumber,
    [EField.NaturalGasMeterType]: property.naturalGasMeterType,

    // Water
    [EField.WaterMeterNumber]: property.waterMeterNumber,

    // Exterior - from propertyAmenities
    [EField.BuildingAdjoined]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.buildingAdjoined,
    ),
    [EField.BuildingSurfaceArea]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingSurfaceArea,
    ),
    [EField.BuildingGardenSurfaceArea]: property.buildingGardenSurfaceArea,
    [EField.BuildingTerraceSurfaceArea]: property.buildingTerraceSurfaceArea,

    // Interior - from propertyAmenities
    [EField.BuildingFloor]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloor,
    ),
    [EField.BuildingFloorCount]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloorCount,
    ),
    [EField.RoomCountBedroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBedroom,
    ),
    [EField.RoomCountBathroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBathroom,
    ),
    [EField.RoomCountGarage]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountGarage,
    ),
    [EField.BuildingHasStorage]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.buildingHasStorage,
      ),
    ),
    [EField.IsFurnished]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isFurnished),
    ),

    // Amenities - from propertyAmenities
    [EField.HasOven]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasOven),
    ),
    [EField.HasFridge]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasFridge),
    ),
    [EField.HasDishWasher]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDishwasher),
    ),
    [EField.HasRangeHood]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasRangeHood),
    ),
    [EField.HasLift]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasLift),
    ),

    // Energy - from propertyAmenities
    [EField.HasDoubleGlazing]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDoubleGlazing),
    ),
    [EField.HasCentralHeating]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.hasCentralHeating,
      ),
    ),
    [EField.HeatingType]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.heatingType,
    ),
  };
};

export const getInitialValues = (extraData: IExtraData): IValues => {
  const { property, contract, registration } = extraData;
  let contractDestination =
    contractLeaseType[extraData.contract.leaseType] ??
    ERegistrationContractDestination.Housing;
  // if (property.typeId === EPropertyTypeId.StudentResidence)
  //   contractDestination = ERegistrationContractDestination.University;
  if (property.typeId === EPropertyTypeId.Commercial) {
    contractDestination = ERegistrationContractDestination.Commercial;
  }
  if (registration) {
    // When retrying a failed registration, include both registration-specific
    // values AND Brussels property/amenity values for proper prefilling
    const registrationValues = {
      [EField.Owners]: registration.members
        .filter(member => member.roles.includes(ERegistrationMemberType.Owner))
        .map(member => member.account.id),
      [EField.SignAsBroker]: registration.members
        .filter(member => member.roles.includes(ERegistrationMemberType.Broker))
        .map(member => member.account.id),
      [EField.Tenants]: registration.members
        .filter(member => member.roles.includes(ERegistrationMemberType.Tenant))
        .map(member => member.account.id),
      [EField.RentContractDocumentToUpload]: registration.documents
        .filter(
          document =>
            document.documentType === ERegistrationDocumentType.Contract,
        )
        .map(document => document.documentId),
      [EField.RentContractLanguage]: registration.preferredContractLanguage,
      [EField.RentContractDestination]: registration.rentContractDestination,
      [EField.RentContractSignedAt]: contract.signedAt,
      [EField.LocationDescriptionShouldUpload]: "now",
      [EField.RentContractPropertyTaxPaidByTenant]:
        registration.rentContractPropertyTaxPaidByTenant,
    };

    // Merge with Brussels property/amenity values for prefilling
    return {
      ...getBrusselsPropertyValues(extraData),
      ...registrationValues,
    };
  }

  // For new registrations, combine base values with Brussels property/amenity values
  return {
    [EField.RentContractDestination]: contractDestination,
    [EField.RentContractSignedAt]: new Date(),
    [EField.RentContractPropertyTaxPaidByTenant]: "yes",
    [EField.LocationDescriptionShouldUpload]: "now",
    [EField.LocationDescriptionSignedAt]: new Date(),
    ...getBrusselsPropertyValues(extraData),
  };
};

export const getRegistrationCompanyFieldsSchema = (contact: IContact) =>
  getAllRequiredCompanyFields({
    contact,
    requiredFields: [],
  });

export const getRegistrationContactFieldsSchema = (contact: IContact) =>
  getAllRequiredContactFields({
    contact,
    requiredFields: [
      EContactField.Address,
      EContactField.DateOfBirth,
      EContactField.Nationality,
      EContactField.PlaceOfBirth,
    ],
    appendIdentityNumberToRequiredFields: true,
  });

export const getDocumentSignedAt = (params: {
  documentField: EField;
  signedAtField: EField;
  initialValues: { [key: string]: unknown };
  defaultValues: { [key: string]: unknown };
  registration?: IRegistration;
}) => {
  const {
    documentField,
    signedAtField,
    initialValues,
    defaultValues,
    registration,
  } = params;

  const documentIds = getValue(documentField, initialValues, defaultValues);

  const documentId = documentIds?.[0];

  if (registration) {
    const contractDocument = registration.documents.find(
      document => document.documentType === ERegistrationDocumentType.Contract,
    );

    if (documentId === contractDocument?.documentId) {
      return contractDocument?.signedAt;
    }
  }

  if (typeof documentId === "string") {
    const store = getStore();
    if (store) {
      const state = store.getState();

      const documentPackage = Object.values(
        state.documentPackage.documentPackages,
      ).find(x => x.documentsInPackageIds.includes(documentId));

      if (documentPackage?.status === EDocumentPackageStatus.Finished) {
        // Find signer that last signed
        const lastSigner = orderBy(
          documentPackage.signers,
          ["completedAt"],
          "desc",
        )[0];
        return lastSigner.completedAt;
      }
    }
  }

  return getValue(signedAtField, initialValues, defaultValues);
};
